import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * OnboardingDialog
 *
 * Full-screen onboarding flow (Notion-style)
 * Shows when a user has no workspace access
 * Two modes:
 * - Regular: Create your first workspace
 * - Invited: Welcome message for accepted invitation
 */

interface OnboardingDialogProps {
  open: boolean;
  mode: "create" | "invited";
  invitedWorkspaceName?: string;
  onCreateWorkspace: (name: string) => Promise<void>;
  onClose?: () => void;
}

export function OnboardingDialog({
  open,
  mode,
  invitedWorkspaceName,
  onCreateWorkspace,
  onClose,
}: OnboardingDialogProps) {
  const [workspaceName, setWorkspaceName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  if (!open) return null;

  const handleCreate = async () => {
    if (!workspaceName.trim()) {
      toast.error("Please enter a workspace name");
      return;
    }

    setIsCreating(true);
    try {
      await onCreateWorkspace(workspaceName.trim());
      setWorkspaceName("");
    } catch (error) {
      console.error("Failed to create workspace:", error);
      toast.error("Failed to create workspace");
    } finally {
      setIsCreating(false);
    }
  };

  if (mode === "invited") {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="max-w-md w-full px-6 space-y-8 text-center">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold">
              Welcome to {invitedWorkspaceName}!
            </h1>
            <p className="text-muted-foreground text-lg">
              You've been added to the workspace. You can now start
              collaborating with your team.
            </p>
          </div>
          <Button onClick={onClose} size="lg" className="w-full">
            Get Started
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
      <div className="max-w-md w-full px-6 space-y-8">
        <div className="space-y-4 text-center">
          <img
            src="/taias_light.png"
            alt="Taias"
            className="h-16 w-auto mx-auto dark:hidden"
          />
          <img
            src="/taias_dark.png"
            alt="Taias"
            className="h-16 w-auto mx-auto hidden dark:block"
          />
          <h1 className="text-3xl font-bold">Welcome to Taias!</h1>
          <p className="text-muted-foreground text-lg">
            Let's get started by creating your first workspace. A workspace is
            where you and your team can collaborate.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              placeholder="My Workspace"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreate();
                }
              }}
              autoFocus
              className="h-12 text-base"
            />
            <p className="text-xs text-muted-foreground">
              You can invite team members later from workspace settings.
            </p>
          </div>

          <Button
            onClick={handleCreate}
            disabled={isCreating}
            size="lg"
            className="w-full"
          >
            {isCreating ? "Creating..." : "Create Workspace"}
          </Button>
        </div>
      </div>
    </div>
  );
}
